import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FileText, BedDouble, Utensils, Users2, TrendingUp,
  Building2, Clock, Calendar, PieChart,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLOR_HEX = {
  emerald: '#10b981', amber: '#f59e0b', lime: '#84cc16', sky: '#0ea5e9',
  indigo: '#6366f1', violet: '#8b5cf6', rose: '#f43f5e', orange: '#f97316',
  slate: '#64748b', teal: '#14b8a6',
};

const fmtCLP = (n) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('es-CL').format(n || 0);
const shortDate = (iso) => {
  const [_, m, d] = iso.split('-');
  return `${d}/${m}`;
};

export default function SgDashboard() {
  const { token } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    axios.get(`${API}/servicios-generales/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setD(r.data)).catch(() => { });
  }, [token]);

  if (!d) return <p className="text-slate-500">Cargando...</p>;

  const kpis = [
    { label: 'Contratos vigentes', value: d.contratos_vigentes, sub: `${d.contratos_total} totales`, color: '#0F766E', icon: FileText },
    { label: 'Habitaciones ocupadas', value: d.habitaciones_ocupadas, sub: `${d.habitaciones_activas} activas`, color: '#047857', icon: BedDouble },
    { label: 'Casino hoy', value: d.casino_usos_hoy, sub: `${d.casino_personas_hoy} personas distintas`, color: '#059669', icon: Utensils },
    { label: 'Personas casino hoy', value: d.casino_personas_hoy, sub: 'Únicas', color: '#2563EB', icon: Users2 },
  ];

  return (
    <div className="space-y-6" data-testid="sg-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">Panorama general de Casino y Hotelería · últimos 30 días.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${k.color}15`, border: `1px solid ${k.color}30` }}>
                <k.icon className="w-5 h-5" style={{ color: k.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k.label}</p>
                <p className="text-2xl font-black text-slate-900">{k.value}</p>
                <p className="text-[10px] text-slate-500 truncate">{k.sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fila 1 · Tendencia casino y ocupación hotelería */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Tendencia Casino (14 días)" icon={TrendingUp}>
          <TendenciaBarras rows={d.tendencia_casino} />
        </Card>
        <Card title="Ocupación Hotelería (14 días)" icon={BedDouble}>
          <OcupacionArea rows={d.ocupacion_hoteleria} total={d.habitaciones_activas} />
        </Card>
      </div>

      {/* Fila 2 · Comidas de hoy · Distribución tipos · Franja horaria */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card title="Comidas de hoy" icon={Calendar}>
          <ListaComidasHoy rows={d.comidas_hoy} />
        </Card>
        <Card title="Distribución tipos (30d)" icon={PieChart}>
          <DonutTipos rows={d.distribucion_tipos} />
        </Card>
        <Card title="Franja horaria (30d)" icon={Clock}>
          <FranjaHoraria rows={d.distribucion_horaria} />
        </Card>
      </div>

      {/* Fila 3 · Top empresas (barra horizontal) + Pabellones (ocupación) */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Card title="Top empresas por consumo (30d)" icon={Building2}>
            <TopEmpresas rows={d.top_empresas} />
          </Card>
        </div>
        <Card title="Ocupación por pabellón" icon={BedDouble}>
          <PabellonBarras rows={d.por_pabellon} />
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/70 flex items-center gap-2">
        <Icon className="w-4 h-4 text-emerald-700" />
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/* ---------- Tendencia Casino: barras con hover tooltip ---------- */
function TendenciaBarras({ rows }) {
  if (!rows || rows.length === 0) return <Empty />;
  const max = Math.max(...rows.map(r => r.total || 0), 1);
  const totalUsos = rows.reduce((a, r) => a + (r.total || 0), 0);
  const totalMonto = rows.reduce((a, r) => a + (r.monto || 0), 0);
  return (
    <div>
      <div className="flex items-end gap-1.5" style={{ height: 160 }}>
        {rows.map(r => {
          const h = Math.max(6, Math.round((r.total / max) * 140));
          return (
            <div key={r.fecha} className="flex-1 h-full flex flex-col justify-end items-center group relative" title={`${r.fecha}\n${r.total} usos · ${fmtCLP(r.monto)}`}>
              <span className="absolute -top-1 opacity-0 group-hover:opacity-100 text-[10px] font-bold text-emerald-900 bg-white px-1.5 py-0.5 rounded shadow-sm border border-emerald-100 whitespace-nowrap pointer-events-none z-10 transition-opacity">
                {r.total} · {fmtCLP(r.monto)}
              </span>
              <div className="w-full rounded-t-md bg-gradient-to-t from-emerald-700 via-emerald-500 to-emerald-300 hover:from-emerald-800 hover:to-emerald-400 transition-colors" style={{ height: h }} />
              <span className="text-[9px] text-slate-500 font-mono mt-1 leading-none">{shortDate(r.fecha)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-2 border-t border-slate-100 flex justify-between text-[11px] text-slate-500">
        <span>Total <b className="text-slate-800">{fmtN(totalUsos)}</b> usos</span>
        <span>Ingresos <b className="text-emerald-800">{fmtCLP(totalMonto)}</b></span>
        <span>Máx día <b className="text-slate-800">{max}</b></span>
      </div>
    </div>
  );
}

/* ---------- Ocupación Hotelería: área ---------- */
function OcupacionArea({ rows, total }) {
  if (!rows || rows.length === 0) return <Empty />;
  const W = 520, H = 160, PAD = 24;
  const maxY = Math.max(total || 1, ...rows.map(r => r.ocupadas || 0), 1);
  const stepX = (W - PAD * 2) / Math.max(1, rows.length - 1);
  const pts = rows.map((r, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - ((r.ocupadas || 0) / maxY) * (H - PAD * 2);
    return [x, y];
  });
  const dLine = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const dArea = `${dLine} L${pts[pts.length - 1][0]},${H - PAD} L${pts[0][0]},${H - PAD} Z`;
  const avg = Math.round(rows.reduce((a, r) => a + (r.ocupadas || 0), 0) / rows.length);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40">
        <defs>
          <linearGradient id="g-hot" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map(t => (
          <line key={t} x1={PAD} x2={W - PAD} y1={H - PAD - t * (H - PAD * 2)} y2={H - PAD - t * (H - PAD * 2)} stroke="#e2e8f0" strokeDasharray="2 2" />
        ))}
        <path d={dArea} fill="url(#g-hot)" />
        <path d={dLine} fill="none" stroke="#059669" strokeWidth="2.2" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="3" fill="#fff" stroke="#059669" strokeWidth="2">
              <title>{`${rows[i].fecha} · ${rows[i].ocupadas} ocupadas`}</title>
            </circle>
          </g>
        ))}
        {rows.map((r, i) => (
          <text key={i} x={PAD + i * stepX} y={H - 4} fontSize="9" textAnchor="middle" fill="#94a3b8" fontFamily="monospace">{shortDate(r.fecha)}</text>
        ))}
      </svg>
      <div className="mt-1 pt-2 border-t border-slate-100 flex justify-between text-[11px] text-slate-500">
        <span>Promedio <b className="text-slate-800">{avg}</b></span>
        <span>Máx día <b className="text-emerald-800">{maxY}</b></span>
        <span>Capacidad <b className="text-slate-800">{total}</b></span>
      </div>
    </div>
  );
}

/* ---------- Comidas de hoy ---------- */
function ListaComidasHoy({ rows }) {
  if (!rows || rows.length === 0) return <Empty label="Sin registros hoy." />;
  const total = rows.reduce((a, r) => a + (r.c || 0), 0);
  return (
    <div className="space-y-2">
      {rows.map((c, i) => {
        const pct = total ? Math.round((c.c / total) * 100) : 0;
        return (
          <div key={i}>
            <div className="flex items-center justify-between text-sm mb-0.5">
              <span className="capitalize text-slate-700">{c.comida}</span>
              <span className="text-slate-500 text-xs"><b className="text-slate-900">{c.c}</b> · {pct}%</span>
            </div>
            <div className="h-2 rounded bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
      <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-500">Total <b className="text-slate-800">{total}</b></div>
    </div>
  );
}

/* ---------- Donut tipos ---------- */
function DonutTipos({ rows }) {
  if (!rows || rows.length === 0) return <Empty />;
  const total = rows.reduce((a, r) => a + (r.usos || 0), 0) || 1;
  const R = 52, R2 = 38, C = 80;
  let acc = 0;
  const segments = rows.map(r => {
    const frac = (r.usos || 0) / total;
    const start = acc; acc += frac;
    const a1 = start * 2 * Math.PI - Math.PI / 2;
    const a2 = acc * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = C + R * Math.cos(a1), y1 = C + R * Math.sin(a1);
    const x2 = C + R * Math.cos(a2), y2 = C + R * Math.sin(a2);
    const x3 = C + R2 * Math.cos(a2), y3 = C + R2 * Math.sin(a2);
    const x4 = C + R2 * Math.cos(a1), y4 = C + R2 * Math.sin(a1);
    return { r, path: `M${x1},${y1} A${R},${R} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${R2},${R2} 0 ${large} 0 ${x4},${y4} Z` };
  });
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 160 160" className="w-32 h-32 shrink-0">
        {segments.map((s, i) => (
          <path key={i} d={s.path} fill={COLOR_HEX[s.r.color] || COLOR_HEX.slate}>
            <title>{`${s.r.tipo}: ${s.r.usos} usos`}</title>
          </path>
        ))}
        <text x="80" y="76" textAnchor="middle" fontSize="11" fill="#94a3b8" fontWeight="600">Total</text>
        <text x="80" y="94" textAnchor="middle" fontSize="18" fill="#0f172a" fontWeight="800">{fmtN(total)}</text>
      </svg>
      <div className="flex-1 space-y-1.5 min-w-0">
        {rows.map((r, i) => {
          const pct = Math.round((r.usos / total) * 100);
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLOR_HEX[r.color] || COLOR_HEX.slate }} />
              <span className="text-slate-700 truncate flex-1">{r.tipo}</span>
              <span className="font-mono text-slate-500">{pct}%</span>
              <span className="font-bold text-slate-800 w-8 text-right">{r.usos}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Franja horaria ---------- */
function FranjaHoraria({ rows }) {
  if (!rows || rows.length === 0) return <Empty />;
  const bins = [
    { h: 6, label: '06-08', desc: 'Desayuno' },
    { h: 8, label: '08-10', desc: 'Desayuno' },
    { h: 10, label: '10-12', desc: 'Colación AM' },
    { h: 12, label: '12-14', desc: 'Almuerzo' },
    { h: 14, label: '14-16', desc: 'Tarde' },
    { h: 16, label: '16-18', desc: 'Colación PM' },
    { h: 18, label: '18-20', desc: 'Cena' },
    { h: 20, label: '20-22', desc: 'Cena' },
  ];
  const map = new Map(rows.map(r => [r.h, r.c]));
  const dataset = bins.map(b => ({ ...b, c: map.get(b.h) || 0 }));
  const max = Math.max(...dataset.map(x => x.c), 1);
  return (
    <div>
      <div className="flex items-end gap-1.5" style={{ height: 130 }}>
        {dataset.map(b => {
          const h = Math.max(4, Math.round((b.c / max) * 110));
          return (
            <div key={b.h} className="flex-1 h-full flex flex-col justify-end items-center group relative"
                 title={`${b.label} · ${b.desc}: ${b.c} usos`}>
              <span className="absolute -top-1 opacity-0 group-hover:opacity-100 text-[10px] font-bold text-sky-800 bg-white px-1 py-0.5 rounded shadow-sm border border-sky-100 whitespace-nowrap pointer-events-none z-10 transition-opacity">
                {b.c}
              </span>
              <div className="w-full rounded-t bg-gradient-to-t from-sky-600 to-sky-300 hover:from-sky-700 hover:to-sky-400 transition-colors" style={{ height: h }} />
              <span className="text-[8px] text-slate-500 font-mono mt-1 leading-none">{b.label}</span>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-slate-500 text-center">Franjas de 2h · Total <b className="text-slate-800">{fmtN(dataset.reduce((a, b) => a + b.c, 0))}</b></p>
    </div>
  );
}

/* ---------- Top empresas: barra horizontal ---------- */
function TopEmpresas({ rows }) {
  if (!rows || rows.length === 0) return <Empty />;
  const max = Math.max(...rows.map(r => r.usos || 0), 1);
  const palette = ['#059669', '#0891b2', '#7c3aed', '#f59e0b', '#e11d48', '#0284c7', '#65a30d', '#c026d3'];
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const pct = Math.round((r.usos / max) * 100);
        return (
          <div key={i}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="text-slate-700 truncate pr-2 flex-1">{r.empresa}</span>
              <span className="font-bold text-slate-800">{fmtN(r.usos)}</span>
              <span className="text-slate-500 ml-2 w-20 text-right">{fmtCLP(r.monto)}</span>
            </div>
            <div className="h-2 rounded bg-slate-100 overflow-hidden">
              <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: palette[i % palette.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Ocupación por pabellón ---------- */
function PabellonBarras({ rows }) {
  if (!rows || rows.length === 0) return <Empty />;
  return (
    <div className="space-y-3">
      {rows.map((p, i) => {
        const pct = p.activas > 0 ? Math.round((p.ocupadas / p.activas) * 100) : 0;
        return (
          <div key={i}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-bold text-slate-800">{p.edificio}</span>
              <span className="text-slate-500">{p.ocupadas}/{p.activas} <span className="text-slate-400">· {pct}%</span></span>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${pct >= 85 ? 'bg-rose-500' : pct >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">{p.total} habitaciones totales</div>
          </div>
        );
      })}
    </div>
  );
}

function Empty({ label = 'Sin datos.' }) {
  return <div className="py-6 text-sm text-slate-400 text-center">{label}</div>;
}
